import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Container,
  CircularProgress,
} from '@mui/material';
import { Routes, Route, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePropertyId, usePropertyData } from '../../common/hooks/usePropertyId';
import { useRoomsData } from '../../common/hooks/useRoomsData';
import { useReservationsData } from '../../common/hooks/useReservationsData';
import { usePaymentsData } from '../../common/hooks/usePaymentsData';
import { useBankTransferConfigData } from '../../common/hooks/useBankTransferConfigData';
import { QueryData, QueryMultipleData } from '../../common/withQueryData';
import { RESERVATION_STATUS } from '../../common/enums';
import ReservationDetails from './components/reservation/ReservationDetails';
import {
  PAYMENT_ERROR_MESSAGES,
  applyOptimisticPayuPayments,
  buildThankYouMessage,
  formatReservationStatus,
  parsePayuPaymentIds,
} from './components/reservation/reservationUtils';
import PayuPaymentSuccessPage from './PayuPaymentSuccessPage';
import PayuPaymentFailurePage from './PayuPaymentFailurePage';
import ReservationCheckoutPage from './ReservationCheckoutPage';

const ReservationQueryError = ({ onReload }) => {
  const navigate = useNavigate();

  return (
    <Container sx={{ py: 8, maxWidth: 'lg' }}>
      <Alert severity="error" sx={{ mb: 2 }}>
        Nie udało się załadować szczegółów rezerwacji.
      </Alert>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={onReload}>
          Spróbuj ponownie
        </Button>
        <Button variant="outlined" onClick={() => navigate('/book')}>
          Wróć do rezerwacji
        </Button>
      </Box>
    </Container>
  );
};

/**
 * Unified reservation page at /reservation/:reservationId.
 * DRAFT orders redirect to /reservation/:id/checkout.
 * Other statuses → confirmation view with payment schedule and bank details.
 */
const ReservationConfirmation = ({
  order,
  payments,
  rooms,
  bankTransferConfig,
  propertyName,
  refetchReservation,
  refetchPayments,
}) => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [bannerError, setBannerError] = useState(null);
  const [optimisticPayuIds, setOptimisticPayuIds] = useState([]);

  const isDraft = order?.status === RESERVATION_STATUS.DRAFT;
  const isConfirmed = order?.status === RESERVATION_STATUS.CONFIRMED;
  const isPendingPayment = order?.status === RESERVATION_STATUS.PENDING_PAYMENT;
  const showPayuProcessing = isPendingPayment && optimisticPayuIds.length > 0;

  useEffect(() => {
    if (isDraft) {
      navigate(
        { pathname: `/reservation/${reservationId}/checkout`, search: searchParams.toString() },
        { replace: true }
      );
    }
  }, [isDraft, reservationId, navigate, searchParams]);

  useEffect(() => {
    if (isDraft) return;

    const paymentError = searchParams.get('paymentError');
    const payuPaymentId = searchParams.get('payuPaymentId');

    if (!paymentError && !payuPaymentId) return;

    if (paymentError && PAYMENT_ERROR_MESSAGES[paymentError]) {
      setBannerError(PAYMENT_ERROR_MESSAGES[paymentError]);
    }
    if (payuPaymentId) {
      setOptimisticPayuIds(parsePayuPaymentIds(payuPaymentId));
    }
  }, [searchParams, setSearchParams, isDraft]);

  useEffect(() => {
    if (!showPayuProcessing || isConfirmed) return undefined;

    const poll = () => {
      refetchReservation();
      refetchPayments();
    };

    const intervalId = setInterval(poll, 5000);
    return () => clearInterval(intervalId);
  }, [showPayuProcessing, isConfirmed, refetchReservation, refetchPayments]);

  useEffect(() => {
    if (isConfirmed && optimisticPayuIds.length > 0) {
      setOptimisticPayuIds([]);
    }
  }, [isConfirmed, optimisticPayuIds.length]);

  const room = rooms.find(
    (r) => r.id === order?.roomId || String(r.id) === String(order?.roomId)
  );

  const displayPayments = useMemo(
    () => applyOptimisticPayuPayments(payments, optimisticPayuIds),
    [payments, optimisticPayuIds]
  );

  const thankYouMessage = buildThankYouMessage(order?.status, propertyName);
  const reservationStatusLabel = formatReservationStatus(order?.status);

  if (isDraft) return null;

  return (
    <Container sx={{ py: 8, maxWidth: 'lg' }}>
      <Box>
        {bannerError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBannerError(null)}>
            {bannerError}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={600}
            sx={{ color: '#2d5016', mb: 1.5 }}
          >
            Potwierdzenie rezerwacji
          </Typography>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {thankYouMessage}
          </Typography>
          <Typography variant="body1">
            <strong>Status rezerwacji:</strong> {reservationStatusLabel}
          </Typography>
        </Box>

        {showPayuProcessing && (
          <Alert
            severity="info"
            icon={<CircularProgress size={22} color="inherit" />}
            sx={{ mb: 3, '& .MuiAlert-icon': { alignItems: 'center' } }}
          >
            <Typography variant="body2">
              Płatność PayU została przyjęta i jest przetwarzana. Potwierdzenie e-mail zostanie
              wysłane po zaksięgowaniu środków.
            </Typography>
          </Alert>
        )}

        <ReservationDetails
          order={order}
          roomName={room?.name}
          payments={displayPayments}
          bankTransferConfig={bankTransferConfig}
          showPaymentMethodColumn
          showConfirmationNumber
        />
      </Box>
    </Container>
  );
};

const ReservationConfirmationPage = () => {
  const { reservationId } = useParams();
  const propertyId = usePropertyId();
  const navigate = useNavigate();
  const { queryData: propertyQuery } = usePropertyData();

  const { queryResults: reservationQuery } = useReservationsData(propertyId, reservationId);
  const { queryResults: paymentsQuery } = usePaymentsData(propertyId, reservationId);
  const { queryResults: roomsQuery } = useRoomsData(propertyId);
  const { queryResults: bankTransferQuery } = useBankTransferConfigData(propertyId);

  if (!reservationId) {
    return (
      <Container sx={{ py: 8, maxWidth: 'lg' }}>
        <Alert severity="error" sx={{ mb: 2 }}>Brak identyfikatora rezerwacji.</Alert>
        <Button variant="outlined" onClick={() => navigate('/book')}>Wróć do rezerwacji</Button>
      </Container>
    );
  }

  return (
    <QueryMultipleData
      queries={{
        reservation: reservationQuery,
        payments: paymentsQuery,
        rooms: roomsQuery,
        bankTransfer: bankTransferQuery,
        property: propertyQuery,
      }}
      error={ReservationQueryError}
    >
      {(queryResults) => (
        <ReservationConfirmation
          order={queryResults.reservation.data}
          payments={queryResults.payments.data ?? []}
          rooms={queryResults.rooms.data ?? []}
          bankTransferConfig={queryResults.bankTransfer.data}
          propertyName={queryResults.property?.data?.name}
          refetchReservation={queryResults.reservation.refetch}
          refetchPayments={queryResults.payments.refetch}
        />
      )}
    </QueryMultipleData>
  );
};

const ReservationPage = () => {
  const { reservationId } = useParams();
  const propertyId = usePropertyId();

  const { queryResults: reservationQuery } = useReservationsData(propertyId, reservationId);

  return <QueryData query={reservationQuery} error={ReservationQueryError}>
    {(reservation) => (
      <Routes>
        <Route path="payment/success" element={<PayuPaymentSuccessPage />} />
        <Route path="payment/failure" element={<PayuPaymentFailurePage />} />
        <Route path="checkout" element={<ReservationCheckoutPage reservation={reservation} />} />
        <Route path="*" element={<ReservationConfirmationPage />} />
      </Routes>
    )}
  </QueryData>
};

export default ReservationPage;
